"use client";

import Image from "next/image";
import Link from "next/link";
import { LockKeyhole } from "lucide-react";
import { PinkImageFallback } from "@/components/PinkImageFallback";
import type { LockedContentTeaser } from "@/lib/member-content";

function authHref(path: "/auth/login" | "/auth/signup", redirectTo: string) {
  return `${path}?redirectTo=${encodeURIComponent(redirectTo)}`;
}

export function MemberOnlyCard({
  teaser,
  redirectTo
}: {
  teaser: LockedContentTeaser;
  redirectTo: string;
}) {
  return (
    <article className="relative flex min-h-[300px] flex-col overflow-hidden rounded-2xl border border-nv-red/25 bg-card shadow-soft">
      <div className="relative aspect-[16/10] overflow-hidden bg-nv-ink">
        {teaser.imageUrl ? (
          <Image src={teaser.imageUrl} alt="" fill sizes="(min-width: 1024px) 30vw, 90vw" className="scale-105 object-cover blur-[3px] opacity-70" />
        ) : (
          <PinkImageFallback ariaLabel={`${teaser.title} image unavailable`} headline={null} footer={teaser.entityType} className="h-full w-full" />
        )}
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-nv-ink/90 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-white">
          <LockKeyhole className="h-3.5 w-3.5" /> Members only
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-nv-red">{teaser.entityType}</p>
        <h2 className="mt-2 font-display text-2xl leading-tight">{teaser.title}</h2>
        <div className="relative mt-4 overflow-hidden rounded-lg border border-border bg-muted/40 p-4">
          <div aria-hidden className="space-y-2 blur-[4px]">
            <div className="h-3 w-full rounded bg-muted-foreground/25" />
            <div className="h-3 w-4/5 rounded bg-muted-foreground/20" />
          </div>
          <p className="absolute inset-0 flex items-center justify-center text-center text-sm font-bold">Sign in to continue</p>
        </div>
        <div className="mt-auto grid grid-cols-2 gap-2 pt-5">
          <Link href={authHref("/auth/login", redirectTo)} className="nv-button-secondary min-h-10 justify-center px-3 text-xs">Sign in</Link>
          <Link href={authHref("/auth/signup", redirectTo)} className="nv-button min-h-10 justify-center px-3 text-xs">Sign up</Link>
        </div>
      </div>
    </article>
  );
}

export function MemberOnlyRow({ teaser, redirectTo }: { teaser: LockedContentTeaser; redirectTo: string }) {
  return (
    <div className="grid min-w-[760px] grid-cols-[1.5fr_1fr_1fr_1fr_120px] items-center border-b border-border px-4 py-4 text-sm last:border-0">
      <div>
        <p className="font-semibold">{teaser.title}</p>
        <p className="mt-0.5 text-xs text-nv-red">Members only</p>
      </div>
      <span className="capitalize text-muted-foreground">{teaser.entityType}</span>
      <span className="text-muted-foreground">Sign in to view</span>
      <span className="text-muted-foreground">Protected</span>
      <Link href={authHref("/auth/login", redirectTo)} className="text-right font-semibold text-nv-red hover:underline">Sign in</Link>
    </div>
  );
}
